/**
 * i18n FR / EN / ES (demande utilisateur 23/06/2026). La langue est choisie UNE
 * fois au démarrage, par ordre de priorité :
 *   1. choix MANUEL mémorisé (localStorage, prioritaire) ;
 *   2. langue du NAVIGATEUR (navigator.languages) si FR/EN/ES ;
 *   3. PAYS source (GeoIP serveur via /api/geo) → espagnol si pays hispanophone ;
 *   4. repli : ANGLAIS.
 * Changer la langue (sélecteur) RECHARGE la page → tout est rendu dans la nouvelle
 * langue (pas de re-rendu dynamique à maintenir).
 *
 * NB : `current` vaut « fr » AVANT `initLang()` (les tests purs n'appellent pas
 * initLang → restent en français, comme les chaînes d'origine) ; l'app, elle,
 * `await initLang()` AVANT le rendu, donc l'utilisateur ne voit jamais ce défaut.
 */
import fr from "../i18n/fr.ts";
import en from "../i18n/en.ts";
import es from "../i18n/es.ts";

export type Lang = "fr" | "en" | "es";
const SUPPORTED: readonly Lang[] = ["fr", "en", "es"];
const FALLBACK: Lang = "en"; // repli quand rien ne correspond (décision utilisateur)
const STORE_KEY = "wc26.lang";
const TAG: Record<Lang, string> = { fr: "fr-FR", en: "en-GB", es: "es-ES" };
const CATALOGS: Record<Lang, Record<string, string>> = { fr, en, es };
// Pays hispanophones (ISO 3166-1 alpha-2) → on propose l'espagnol via le pays.
const ES_COUNTRIES = new Set(
  "ES MX AR CO PE VE CL EC GT CU BO DO HN PY SV NI CR PA UY PR GQ".split(" "),
);

let current: Lang = "fr";

const isLang = (v: unknown): v is Lang =>
  typeof v === "string" && (SUPPORTED as readonly string[]).includes(v);

function stored(): Lang | null {
  try {
    const v = localStorage.getItem(STORE_KEY);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
}

function fromBrowser(): Lang | null {
  const langs =
    typeof navigator !== "undefined"
      ? navigator.languages?.length
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : []
      : [];
  for (const l of langs) {
    const base = (l || "").slice(0, 2).toLowerCase();
    if (isLang(base)) return base;
  }
  return null;
}

export function lang(): Lang {
  return current;
}
/** Tag BCP-47 de la langue courante (pour Intl.DateTimeFormat / NumberFormat). */
export function localeTag(): string {
  return TAG[current];
}
export function languages(): readonly Lang[] {
  return SUPPORTED;
}

/**
 * Détermine la langue AVANT le rendu. Synchrone (choix mémorisé / navigateur)
 * sauf si rien ne correspond : on interroge alors le pays (GeoIP, /api/geo).
 */
export async function initLang(): Promise<Lang> {
  const s = stored();
  if (s) return (current = s);
  const b = fromBrowser();
  if (b) return (current = b);
  try {
    const r = await fetch("/api/geo", { cache: "no-store" });
    if (r.ok) {
      const d = (await r.json()) as { country?: string };
      if (d.country && ES_COUNTRIES.has(d.country.toUpperCase())) return (current = "es");
    }
  } catch {
    /* pas de géo (dev / hors-ligne) → repli */
  }
  return (current = FALLBACK);
}

/** Sélecteur manuel : mémorise le choix puis RECHARGE (rendu dans la nouvelle langue). */
export function setLang(l: Lang): void {
  if (!isLang(l) || l === current) return;
  try {
    localStorage.setItem(STORE_KEY, l);
  } catch {
    /* stockage indispo → on recharge quand même (langue de session) */
  }
  location.reload();
}

/** Traduit une clé (repli : anglais, puis la clé brute). `{x}` interpolé via `params`. */
export function t(key: string, params?: Record<string, string | number>): string {
  const s = CATALOGS[current][key] ?? CATALOGS[FALLBACK][key] ?? key;
  return params ? s.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? `{${k}}`)) : s;
}

/** Pluriel (FR/EN/ES : singulier `.one` si n ≤ 1, sinon `.other`). */
export function tp(baseKey: string, n: number, params?: Record<string, string | number>): string {
  return t(`${baseKey}.${n > 1 ? "other" : "one"}`, { n, ...params });
}
